import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Edit2, Plus } from "lucide-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Form, Modal, Select, Input as AntdInput } from "antd";
import {
  DropdownService,
  SchemaService,
  DynamicModuleService,
  PtsProjectService,
} from "@/services/APIs";
import { get } from "@/services/axiosUitils";
import { notify } from "@/components/common/Notification";
import { removeMetaFields } from "@/lib/removeMetaFields";
import { Button } from "../../components/ui/button";

const TASK_SCHEMA_NAME = "PTS Task";
const EMPLOYEE_SCHEMA_NAME = "PTS Emp";
const STATUS_SCHEMA_NAME = "PTS Status";
// const TASK_CATEGORY_SCHEMA_NAME = "PTS Task Category";
const WORK_TYPE_SCHEMA_NAME = "PTS Work Type";

export type SubtaskListItem = {
  value: string;
  label: string;
  estimatedhours?: number | null;
  assignedEmployeeIds?: string[];
  /** From sub-task API `worked_hours` (e.g. "0:00") */
  worked_hours?: string | null;
  /** From sub-task API `servicedesk_uid` (e.g. "TXT-10448") */
  servicedesk_uid?: string | null;
};

type SpentHoursSubtaskModalProps = {
  open: boolean;
  onClose: () => void;
  projectId: string | null;
  parentTaskId: string | null;
  parentTaskLabel: string;
  /** From project-task API `worked_hours` for the parent row (e.g. "0:00") */
  parentTaskWorkedHours?: string | null;
  subtasks: SubtaskListItem[];
  defaultEmployeeId: string | null | undefined;
  onSuccess: () => void;
};

function findFieldNameFromGrid(
  grid: Array<{ field_name: string; field_title?: string }> | undefined,
  searchTerms: string[]
): string | null {
  if (!grid?.length) {
    return null;
  }
  for (const field of grid) {
    const fieldName = field.field_name.toLowerCase();
    const fieldTitle = field.field_title?.toLowerCase() ?? "";
    if (
      searchTerms.some(
        term =>
          fieldName.includes(term.toLowerCase()) ??
          fieldTitle.includes(term.toLowerCase())
      )
    ) {
      return field.field_name;
    }
  }
  return null;
}

export function SpentHoursSubtaskModal({
  open,
  onClose,
  projectId,
  parentTaskId,
  parentTaskLabel,
  parentTaskWorkedHours,
  subtasks,
  defaultEmployeeId,
  onSuccess,
}: SpentHoursSubtaskModalProps) {
  const queryClient = useQueryClient();
  const [taskForm] = Form.useForm();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editingTaskId, setEditingTaskId] = useState<string | null>(null);
  const [selectedEmployeeIds, setSelectedEmployeeIds] = useState<string[]>([]);
  const [assignedEmployees, setAssignedEmployees] = useState<
    Array<{ id: string; name: string }>
  >([]);
  const [modalProject, setModalProject] = useState<string | null>(null);

  const { data: columnGridData } = useQuery({
    queryKey: ["columnGridData", TASK_SCHEMA_NAME],
    queryFn: () => SchemaService.getColumnGridData(TASK_SCHEMA_NAME),
    enabled: open,
  });

  const { data: projectOptions } = useQuery({
    queryKey: ["open-project-list"],
    queryFn: () => PtsProjectService.getOpenProjectList(),
    enabled: open,
  });

  const { data: parent_taskOptions, isLoading: parent_tasksLoading } = useQuery(
    {
      queryKey: ["project-root-task", modalProject],
      queryFn: async () => {
        if (!modalProject) {
          return [];
        }
        const response = await get<any>("/pts_app/project-root-task/", {
          params: { project_id: modalProject },
        });
        if (
          response?.success &&
          response?.tasks &&
          Array.isArray(response.tasks)
        ) {
          return response.tasks.map((task: any) => ({
            value: String(task.id),
            label: task.taskname ?? task.name ?? String(task.id),
          }));
        }
        if (Array.isArray(response)) {
          return response.map((task: any) => ({
            value: String(task.id ?? task.value),
            label: task.taskname ?? task.name ?? task.label ?? String(task.id),
          }));
        }
        return [];
      },
      enabled: open && !!modalProject,
    }
  );

  const { data: employeeOptions } = useQuery({
    queryKey: ["employee-dropdown", EMPLOYEE_SCHEMA_NAME],
    queryFn: () => DropdownService.getDropdownData(EMPLOYEE_SCHEMA_NAME),
    enabled: open && !!EMPLOYEE_SCHEMA_NAME,
  });

  const { data: employeeDataResponse } = useQuery({
    queryKey: ["employee-data", EMPLOYEE_SCHEMA_NAME],
    queryFn: () =>
      DynamicModuleService.getGridColItems(EMPLOYEE_SCHEMA_NAME, 1, 1000, {}),
    enabled: open && !!EMPLOYEE_SCHEMA_NAME,
  });

  const employeeRecords = useMemo(() => {
    if (!employeeDataResponse) {
      return [];
    }
    if (Array.isArray(employeeDataResponse)) {
      return employeeDataResponse;
    }
    if (
      employeeDataResponse.results &&
      Array.isArray(employeeDataResponse.results)
    ) {
      return employeeDataResponse.results;
    }
    return [];
  }, [employeeDataResponse]);

  const { data: employeeColumnGridData } = useQuery({
    queryKey: ["columnGridData", EMPLOYEE_SCHEMA_NAME],
    queryFn: () => SchemaService.getColumnGridData(EMPLOYEE_SCHEMA_NAME),
    enabled: open,
  });

  const findEmployeeFieldName = useCallback(
    (searchTerms: string[]) => {
      return findFieldNameFromGrid(employeeColumnGridData?.grid, searchTerms);
    },
    [employeeColumnGridData]
  );

  const employeeFirstNameField = findEmployeeFieldName([
    "first_name",
    "firstname",
    "fname",
  ]);
  const employeeLastNameField = findEmployeeFieldName([
    "last_name",
    "lastname",
    "lname",
  ]);

  const employeeOptionsWithNames = useMemo(() => {
    if (!employeeOptions || !employeeRecords.length) {
      return employeeOptions ?? [];
    }
    return employeeOptions.map((employee: { value: string; label: string }) => {
      const employeeRecord = employeeRecords.find(
        (record: any) =>
          String(
            record?.id ?? record?.ID ?? record?.Id ?? record?._id ?? ""
          ) === String(employee.value)
      );
      if (employeeRecord) {
        const firstName =
          (employeeFirstNameField && employeeRecord[employeeFirstNameField]) ??
          employeeRecord.first_name ??
          employeeRecord.firstname ??
          employeeRecord.fname ??
          "";
        const lastName =
          (employeeLastNameField && employeeRecord[employeeLastNameField]) ??
          employeeRecord.last_name ??
          employeeRecord.lastname ??
          employeeRecord.lname ??
          "";
        const fullName = `${firstName} ${lastName}`.trim();
        return { value: employee.value, label: fullName || employee.label };
      }
      return employee;
    });
  }, [
    employeeOptions,
    employeeRecords,
    employeeFirstNameField,
    employeeLastNameField,
  ]);

  // const { data: categoryOptions } = useQuery({
  //   queryKey: ["category-dropdown", TASK_CATEGORY_SCHEMA_NAME],
  //   queryFn: () => DropdownService.getDropdownData(TASK_CATEGORY_SCHEMA_NAME),
  //   enabled: open && !!TASK_CATEGORY_SCHEMA_NAME,
  // });

  const { data: workTypeOptions } = useQuery({
    queryKey: ["work-type-dropdown", WORK_TYPE_SCHEMA_NAME],
    queryFn: () => DropdownService.getDropdownData(WORK_TYPE_SCHEMA_NAME),
    enabled: open && !!WORK_TYPE_SCHEMA_NAME,
  });

  const { data: statusOptions } = useQuery({
    queryKey: ["status-dropdown", STATUS_SCHEMA_NAME],
    queryFn: () => DropdownService.getDropdownData(STATUS_SCHEMA_NAME),
    enabled: open && !!STATUS_SCHEMA_NAME,
  });
  const openStatusValue = useMemo(() => {
    const openOption = statusOptions?.find(
      (s: { value: string; label: string }) =>
        String(s?.label ?? "")
          .trim()
          .toLowerCase() === "open"
    );
    return openOption?.value ? String(openOption.value) : undefined;
  }, [statusOptions]);

  const { data: taskData } = useQuery({
    queryKey: ["task-data-modal", TASK_SCHEMA_NAME, editingTaskId],
    queryFn: () => {
      if (!editingTaskId) {
        throw new Error("Task ID is required");
      }
      return DynamicModuleService.getItemById(TASK_SCHEMA_NAME, editingTaskId);
    },
    enabled: open && !!editingTaskId,
  });

  const isAddMode = !editingTaskId;

  const resetToAddSubtask = useCallback(() => {
    setEditingTaskId(null);
    taskForm.resetFields();
    if (projectId && parentTaskId) {
      setModalProject(projectId);
      const emp = defaultEmployeeId ? [String(defaultEmployeeId)] : [];
      setSelectedEmployeeIds(emp);
      setAssignedEmployees(emp.map(id => ({ id, name: id })));
      taskForm.setFieldsValue({
        project: projectId,
        parent_task: parentTaskId,
        assigned_employees: emp,
        status: openStatusValue,
      });
    }
  }, [projectId, parentTaskId, defaultEmployeeId, taskForm, openStatusValue]);

  const wasOpenRef = useRef(false);
  useEffect(() => {
    if (open && !wasOpenRef.current) {
      resetToAddSubtask();
    }
    wasOpenRef.current = open;
  }, [open, resetToAddSubtask]);

  useEffect(() => {
    if (!open || editingTaskId || !defaultEmployeeId) {
      return;
    }
    if (selectedEmployeeIds.length !== 1) {
      return;
    }
    const id = String(defaultEmployeeId);
    if (String(selectedEmployeeIds[0]) !== id) {
      return;
    }
    const o = employeeOptionsWithNames?.find(
      (e: { value: string; label: string }) => String(e.value) === id
    );
    if (!o) {
      return;
    }
    setAssignedEmployees([{ id, name: o.label }]);
  }, [
    open,
    editingTaskId,
    defaultEmployeeId,
    selectedEmployeeIds,
    employeeOptionsWithNames,
  ]);

  useEffect(() => {
    if (!open || !taskData || !editingTaskId) {
      return;
    }
    const data = removeMetaFields(taskData) as Record<string, unknown>;
    const grid = columnGridData?.grid;

    const taskNameField =
      findFieldNameFromGrid(grid, ["taskname", "task_name", "name", "task"]) ??
      "taskname";
    const projectField =
      findFieldNameFromGrid(grid, ["project", "project_id"]) ?? "project";
    const categoryField =
      findFieldNameFromGrid(grid, [
        "taskcategory",
        "category",
        "task_category",
      ]) ?? "taskcategory";
    const hoursField =
      findFieldNameFromGrid(grid, [
        "taskestimatedhours",
        "estimated_hours",
        "est_hours",
        "hours",
      ]) ?? "taskestimatedhours";
    const parent_taskField =
      findFieldNameFromGrid(grid, ["parent_task", "parent"]) ?? "parent_task";
    const statusField = findFieldNameFromGrid(grid, ["status"]) ?? "status";
    const workTypeField =
      findFieldNameFromGrid(grid, ["work_type", "work_type_id", "work_item"]) ??
      "work_type";
    const emplistField =
      findFieldNameFromGrid(grid, ["emplist", "employee_list", "employees"]) ??
      "emplist";
    const descriptionField =
      findFieldNameFromGrid(grid, [
        "description",
        "desc",
        "task_description",
      ]) ?? "description";

    const proj =
      (projectField && data[projectField]) ?? data.project_id ?? data.project;
    if (proj) {
      setModalProject(String(proj));
    }

    const formValues: Record<string, unknown> = {
      task_name:
        (taskNameField && data[taskNameField]) ??
        data.taskname ??
        data.task_name ??
        data.name,
      project: proj,
      category:
        (categoryField && data[categoryField]) ??
        data.taskcategory ??
        data.category,
      est_hours:
        (hoursField && data[hoursField]) ??
        data.taskestimatedhours ??
        data.est_hours ??
        data.estimated_hours,
      parent_task:
        (parent_taskField && data[parent_taskField]) ??
        data.parent_task ??
        data.parent,
      status:
        (statusField && data[statusField]) ?? data.status ?? openStatusValue,
      work_type:
        (workTypeField && data[workTypeField]) ??
        data.work_type_id ??
        data.work_type ??
        data.work_item,
      description:
        (descriptionField && data[descriptionField] !== undefined
          ? data[descriptionField]
          : null) ??
        data.description ??
        "",
    };

    const emplist = (emplistField && data[emplistField]) ?? data.emplist ?? [];
    if (Array.isArray(emplist) && emplist.length > 0) {
      const employees = emplist.map((emp: { member?: string }) => {
        const employeeId = emp?.member;
        const employee = employeeOptions?.find(
          (e: { value: string }) => String(e.value) === String(employeeId)
        );
        return {
          id: String(employeeId),
          name: employee?.label ?? String(employeeId),
        };
      });
      setAssignedEmployees(employees);
      setSelectedEmployeeIds(employees.map(e => e.id));
      formValues.assigned_employees = employees.map(e => e.id);
    } else {
      setAssignedEmployees([]);
      setSelectedEmployeeIds([]);
      formValues.assigned_employees = [];
    }

    const sel = (v: unknown) =>
      v === undefined || v === null || v === "" ? undefined : String(v);
    formValues.project = sel(formValues.project);
    formValues.category = sel(formValues.category);
    formValues.status = sel(formValues.status);
    formValues.work_type = sel(formValues.work_type);
    formValues.parent_task = sel(formValues.parent_task);

    taskForm.setFieldsValue(formValues);
  }, [
    open,
    taskData,
    editingTaskId,
    columnGridData,
    taskForm,
    employeeOptions,
    openStatusValue,
  ]);

  useEffect(() => {
    if (!open || !openStatusValue || editingTaskId) {
      return;
    }
    taskForm.setFieldValue("status", openStatusValue);
  }, [open, openStatusValue, editingTaskId, taskForm]);

  const handleEmployeeChange = (selectedIds: string[]) => {
    setSelectedEmployeeIds(selectedIds);
    const employees = selectedIds
      .map(id => {
        const employee = employeeOptionsWithNames?.find(
          (emp: { value: string; label: string }) => emp.value === id
        );
        return employee ? { id: employee.value, name: employee.label } : null;
      })
      .filter((emp): emp is { id: string; name: string } => emp !== null);
    setAssignedEmployees(employees);
    taskForm.setFieldsValue({ assigned_employees: selectedIds });
  };

  const handleCancelEditForm = () => {
    resetToAddSubtask();
  };

  const handleSubmitTask = async (values: Record<string, unknown>) => {
    if (!values.project) {
      notify.error("Please select a project");
      return;
    }
    const taskNameTrimmed = String(values.task_name ?? "").trim();
    if (!taskNameTrimmed) {
      notify.error("Please enter task name");
      return;
    }
    if (taskNameTrimmed.replace(/\s/g, "").length > 50) {
      notify.error("Task name must be 50 characters or less");
      return;
    }
    // if (!values.category) {
    //   notify.error("Please select a category");
    //   return;
    // }
    const employeeIdsToSend =
      selectedEmployeeIds.length > 0
        ? selectedEmployeeIds
        : assignedEmployees.map(e => String(e.id));
    if (employeeIdsToSend.length === 0) {
      notify.error("Please assign at least one employee");
      return;
    }

    const grid = columnGridData?.grid;
    const findFn = (terms: string[], fallback: string) =>
      findFieldNameFromGrid(grid, terms) ?? fallback;

    const projectField = findFn(["project", "project_id"], "project");
    const sprintField = findFn(["sprint", "sprint_id"], "sprint");
    const taskNameField = findFn(
      ["taskname", "task_name", "name", "task"],
      "taskname"
    );
    const categoryField = findFn(
      ["taskcategory", "category", "task_category"],
      "taskcategory"
    );
    const hoursField = findFn(
      ["taskestimatedhours", "estimated_hours", "est_hours", "hours"],
      "taskestimatedhours"
    );
    const parent_taskField = findFn(["parent_task", "parent"], "parent_task");
    const statusField = findFn(["status"], "status");
    const workTypeField = findFn(
      ["work_type", "work_type_id", "work_item"],
      "work_type"
    );
    const emplistField = findFn(
      ["emplist", "employee_list", "employees", "assigned_employees"],
      "emplist"
    );
    const descriptionField = findFn(
      ["description", "desc", "task_description"],
      "description"
    );

    setIsSubmitting(true);
    try {
      const now = new Date().toISOString();
      const generateIndex = () =>
        Date.now() * 1000 + Math.floor(Math.random() * 1000);
      const emplist = employeeIdsToSend.map(id => ({
        member: id,
        index: generateIndex(),
        created_on: now,
        updated_on: now,
      }));

      const payload: Record<string, unknown> = {};
      payload[projectField] = values.project;
      payload[taskNameField] = taskNameTrimmed;
      const sprintValue =
        editingTaskId && taskData
          ? ((sprintField &&
              (taskData as Record<string, unknown>)[sprintField]) ??
            (taskData as Record<string, unknown>).sprint_id ??
            (taskData as Record<string, unknown>).sprint ??
            null)
          : (values.sprint ?? null);
      payload[sprintField] = sprintValue;
      payload[categoryField] = values.category;
      payload[hoursField] = values.est_hours ? Number(values.est_hours) : 0;

      payload[parent_taskField] = values.parent_task ?? parentTaskId ?? null;

      payload[statusField] = editingTaskId
        ? (values.status ?? openStatusValue)
        : (openStatusValue ?? values.status);
      payload[workTypeField] = values.work_type ?? null;
      payload[emplistField] = emplist;

      const trimmedDescription =
        values.description !== undefined && values.description !== null
          ? String(values.description).trim()
          : "";
      if (trimmedDescription !== "" && trimmedDescription !== "undefined") {
        payload[descriptionField] = trimmedDescription;
      }

      if (values.est_hours && values.project) {
        const checkParams: Record<string, string | number> = {
          project_id: String(values.project),
          estimated_hours: Number(values.est_hours),
        };
        if (!editingTaskId && values.parent_task) {
          checkParams.task_id = String(values.parent_task);
        }
        if (editingTaskId) {
          checkParams.update_task_id = editingTaskId;
          if (values.parent_task) {
            checkParams.task_id = String(values.parent_task);
          }
        }
        // const validationResponse = await get<any>(
        //   "/pts_app/check-project-estimated/",
        //   { params: checkParams }
        // );
        // const validationData = validationResponse?.data ?? validationResponse;
        // if (validationData?.is_valid === false) {
        //   const errorMessage =
        //     validationData?.message ??
        //     validationResponse?.message ??
        //     "Estimated hours validation failed for this project";
        //   taskForm.setFields([{ name: "est_hours", errors: [errorMessage] }]);
        //   notify.error(errorMessage);
        //   setIsSubmitting(false);
        //   return;
        // }
      }

      if (editingTaskId) {
        await DynamicModuleService.updateItem(
          TASK_SCHEMA_NAME,
          editingTaskId,
          payload
        );
        // notify.success("Task updated");
      } else {
        await DynamicModuleService.createItem(TASK_SCHEMA_NAME, payload);
        // notify.success("Task created");
      }

      queryClient.invalidateQueries({
        queryKey: ["moduleData", TASK_SCHEMA_NAME],
      });
      if (parentTaskId) {
        queryClient.invalidateQueries({
          // queryKey: ["task-subtask", "employee-dashboard", parentTaskId],
          queryKey: ["task-subtask", "employee-dashboard"],
        });
        void queryClient.invalidateQueries({
          queryKey: ["task-subtask", "activity-log-tab", String(parentTaskId)],
        });
      }
      if (projectId) {
        queryClient.invalidateQueries({
          queryKey: ["project-task", "employee-dashboard", projectId],
        });
      }
      onSuccess();
      resetToAddSubtask();
    } catch (error: unknown) {
      const err = error as {
        response?: { data?: { detail?: string; error?: string } };
        message?: string;
      };
      const errorMessage =
        err?.response?.data?.detail ??
        err?.response?.data?.error ??
        err?.message ??
        `Failed to ${editingTaskId ? "update" : "create"} task`;
      notify.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const startEditSubtask = (id: string) => {
    setEditingTaskId(String(id));
  };

  const handleModalClose = () => {
    setEditingTaskId(null);
    taskForm.resetFields();
    onClose();
  };

  if (!projectId || !parentTaskId) {
    return null;
  }

  return (
    <Modal
      title={
        <span className="text-base font-semibold text-[#111827]">
          Tasks for {parentTaskLabel}
        </span>
      }
      open={open}
      onCancel={handleModalClose}
      footer={null}
      width={920}
      destroyOnClose
      className="spent-hours-subtask-modal"
    >
      <style>{`
        .spent-hours-subtask-modal .task-form-select .ant-select-selector {
          background-color: #fafafa !important;
          border-radius: 0.5rem !important;
          border-color: #e5e7eb !important;
        }
        .spent-hours-subtask-modal .task-create-form .ant-form-item-label > label {
          color: #374151;
          font-size: 0.8125rem;
          font-weight: 500;
        }
        .spent-hours-subtask-modal .subtask-scroll {
          scrollbar-width: thin;
          scrollbar-color: #cbd5e1 #f1f5f9;
        }
        .spent-hours-subtask-modal .subtask-scroll::-webkit-scrollbar {
          width: 8px;
        }
        .spent-hours-subtask-modal .subtask-scroll::-webkit-scrollbar-track {
          background: #f1f5f9;
          border-radius: 4px;
        }
        .spent-hours-subtask-modal .subtask-scroll::-webkit-scrollbar-thumb {
          background: #cbd5e1;
          border-radius: 4px;
        }
      `}</style>

      {/* Fixed modal body height so the sub-task list can scroll; form stays fixed (no inner form scroll). */}
      <div className="flex min-h-0 flex-col gap-4 pr-0.5 lg:h-[min(76vh,calc(100dvh-10rem))] lg:max-h-[80vh]">
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
          <div className="flex shrink-0 items-center justify-between gap-2 border-b border-gray-200 bg-gray-50/95 px-3 py-2">
            <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">
              Task list
            </span>
            {subtasks.length > 0 ? (
              <span className="shrink-0 text-xs tabular-nums text-gray-500">
                {subtasks.length} sub task{subtasks.length === 1 ? "" : "s"}
              </span>
            ) : null}
          </div>
          <div className="shrink-0 border-b border-gray-100 bg-white px-3 py-2.5">
            <div className="flex min-w-0 items-center gap-2">
              <span className="inline-flex shrink-0 rounded-full bg-blue-100 px-2 py-0.5 text-[10px] font-semibold uppercase text-blue-800">
                Parent
              </span>
              <span
                className="min-w-0 truncate text-sm font-medium text-gray-900"
                title={parentTaskLabel}
              >
                {parentTaskLabel}
              </span>
              {parentTaskWorkedHours !== null &&
                String(parentTaskWorkedHours).trim() !== "" && (
                  <span className="shrink-0 text-xs tabular-nums text-gray-500">
                    Worked {String(parentTaskWorkedHours).trim()}
                  </span>
                )}
            </div>
          </div>
          <ul className="subtask-scroll min-h-0 flex-1 divide-y divide-gray-100 overflow-y-auto overscroll-y-contain bg-white">
            {subtasks.map(st => {
              const canEditByEmployee =
                !defaultEmployeeId ||
                (Array.isArray(st.assignedEmployeeIds) &&
                  st.assignedEmployeeIds.some(
                    id => String(id) === String(defaultEmployeeId)
                  ));
              return (
                <li
                  key={st.value}
                  className="flex items-center gap-3 px-3 py-2.5 transition-colors hover:bg-gray-50/90"
                >
                  <div className="flex min-w-0 flex-1 items-center gap-2">
                    <span className="inline-flex shrink-0 rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold uppercase text-slate-700">
                      Sub task
                    </span>
                    {st.servicedesk_uid !== null &&
                      st.servicedesk_uid !== undefined &&
                      String(st.servicedesk_uid).trim() !== "" && (
                        <span className="inline-flex shrink-0 items-center rounded-md bg-blue-50 px-1.5 py-0.5 text-[11px] font-semibold text-blue-700">
                          {String(st.servicedesk_uid).trim()}
                        </span>
                      )}
                    <span
                      className="min-w-0 flex-1 truncate text-sm font-medium text-gray-900"
                      title={st.label}
                    >
                      {st.label}
                    </span>
                    {st.estimatedhours !== null && (
                      <span className="shrink-0 text-xs tabular-nums text-gray-500">
                        Est. {st.estimatedhours}h
                      </span>
                    )}
                    {st.worked_hours !== null &&
                      String(st.worked_hours).trim() !== "" && (
                        <span className="shrink-0 text-xs tabular-nums text-gray-500">
                          Worked {String(st.worked_hours).trim()}
                        </span>
                      )}
                  </div>
                  <div className="flex h-8 w-[5.75rem] shrink-0 items-center justify-end">
                    {canEditByEmployee ? (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="h-8 gap-1 px-2.5"
                        onClick={() => startEditSubtask(st.value)}
                      >
                        <Edit2 className="h-3.5 w-3.5" />
                        Edit
                      </Button>
                    ) : (
                      <span className="text-right text-xs text-gray-400">
                        No access
                      </span>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        </div>

        <div className="shrink-0 rounded-lg border border-gray-200 bg-white p-4">
          <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold text-[#111827]">
            {editingTaskId ? (
              <>
                <Edit2 className="h-4 w-4" />
                Edit task
              </>
            ) : (
              <>
                <Plus className="h-4 w-4" />
                Add sub task
              </>
            )}
          </h3>

          <Form
            form={taskForm}
            layout="vertical"
            onFinish={handleSubmitTask}
            className="task-create-form space-y-4"
          >
            <div className="grid grid-cols-1 gap-2 md:grid-cols-2 md:gap-4 lg:grid-cols-3">
              <Form.Item
                name="project"
                label={<span>Project</span>}
                rules={[{ required: true, message: "Please select a project" }]}
              >
                <Select
                  disabled
                  placeholder="Project"
                  className="task-form-select"
                  options={projectOptions?.map(
                    (p: { value: string; label: string }) => ({
                      value: p.value,
                      label: p.label,
                    })
                  )}
                />
              </Form.Item>

              <Form.Item name="parent_task" label={<span>Parent task</span>}>
                <Select
                  allowClear={false}
                  disabled={
                    !modalProject ||
                    parent_tasksLoading ||
                    isAddMode ||
                    !!editingTaskId
                  }
                  placeholder={
                    modalProject ? "Parent task" : "Select a project first"
                  }
                  showSearch
                  className="task-form-select"
                  filterOption={(input, option) => {
                    const label = String(option?.label ?? "");
                    return label.toLowerCase().includes(input.toLowerCase());
                  }}
                  options={parent_taskOptions
                    ?.filter(
                      (task: { value: string }) =>
                        !editingTaskId ||
                        String(task.value) !== String(editingTaskId)
                    )
                    .map((task: { value: string; label: string }) => ({
                      value: task.value,
                      label: task.label,
                    }))}
                />
              </Form.Item>

              {/* <Form.Item
                name="category"
                label={<span>Work area</span>}
                rules={[
                  { required: true, message: "Please select a category" },
                ]}
              >
                <Select
                  allowClear
                  placeholder="Select category"
                  showSearch
                  className="task-form-select"
                  filterOption={(input, option) => {
                    const label = String(option?.label ?? "");
                    return label.toLowerCase().includes(input.toLowerCase());
                  }}
                  options={categoryOptions?.map(
                    (c: { value: string; label: string }) => ({
                      value: c.value,
                      label: c.label,
                    })
                  )}
                />
              </Form.Item> */}

              <Form.Item
                name="status"
                label={<span>Status</span>}
                rules={[{ required: true, message: "Please select a status" }]}
              >
                <Select
                  disabled
                  allowClear={false}
                  placeholder="Open"
                  showSearch={false}
                  className="task-form-select"
                  filterOption={(input, option) => {
                    const label = String(option?.label ?? "");
                    return label.toLowerCase().includes(input.toLowerCase());
                  }}
                  options={statusOptions?.map(
                    (s: { value: string; label: string }) => ({
                      value: s.value,
                      label: s.label,
                    })
                  )}
                />
              </Form.Item>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
              <Form.Item
                name="task_name"
                label={<span>Task name</span>}
                rules={[
                  {
                    validator: async (_, value) => {
                      const s =
                        value === undefined || value === null
                          ? ""
                          : String(value);
                      const trimmed = s.trim();
                      if (!trimmed) {
                        return Promise.reject(
                          new Error("Please enter task name")
                        );
                      }
                      if (trimmed.replace(/\s/g, "").length > 50) {
                        return Promise.reject(
                          new Error("Task name must be 50 characters or less")
                        );
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <AntdInput
                  placeholder="Enter task name"
                  className="h-[2rem] rounded-lg border-gray-200 bg-[#fafafa]"
                />
              </Form.Item>

              <Form.Item
                name="est_hours"
                label={<span>Estimated hours</span>}
                rules={[
                  { required: true, message: "Please enter estimated hours" },
                ]}
              >
                <AntdInput
                  type="number"
                  placeholder="Estimated hours"
                  min={1}
                  className="h-[2rem] rounded-lg border-gray-200 bg-[#fafafa]"
                />
              </Form.Item>

              <Form.Item
                name="work_type"
                label={<span>Work type</span>}
                rules={[
                  { required: true, message: "Please select a work type" },
                ]}
              >
                <Select
                  allowClear
                  placeholder="Select work type"
                  showSearch
                  className="task-form-select"
                  filterOption={(input, option) => {
                    const label = String(option?.label ?? "");
                    return label.toLowerCase().includes(input.toLowerCase());
                  }}
                  options={workTypeOptions?.map(
                    (w: { value: string; label: string }) => ({
                      value: w.value,
                      label: w.label,
                    })
                  )}
                />
              </Form.Item>
            </div>

            <Form.Item
              name="description"
              label={<span>Description (optional)</span>}
            >
              <AntdInput.TextArea
                rows={3}
                placeholder="Task description"
                className="rounded-lg border-gray-200"
              />
            </Form.Item>

            <Form.Item
              name="assigned_employees"
              label={<span>Assign employees</span>}
              rules={[
                {
                  required: true,
                  validator: () => {
                    const count =
                      selectedEmployeeIds.length || assignedEmployees.length;
                    if (count === 0) {
                      return Promise.reject(
                        new Error("Please assign at least one employee")
                      );
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <Select
                disabled
                allowClear={false}
                mode="multiple"
                placeholder="Assignees are fixed for this flow"
                showSearch={false}
                value={selectedEmployeeIds}
                onChange={handleEmployeeChange}
                className="task-form-select w-full max-w-xl"
                filterOption={(input, option) => {
                  const label = String(option?.label ?? "");
                  return label.toLowerCase().includes(input.toLowerCase());
                }}
                options={employeeOptionsWithNames?.map(
                  (e: { value: string; label: string }) => ({
                    value: e.value,
                    label: e.label,
                  })
                )}
              />
            </Form.Item>

            <div className="flex justify-end gap-2 pt-2">
              {editingTaskId && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleCancelEditForm}
                  disabled={isSubmitting}
                >
                  New sub task
                </Button>
              )}
              <Button
                type="button"
                variant="outline"
                onClick={handleModalClose}
                disabled={isSubmitting}
              >
                Close
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting}
                className="bg-primary hover:bg-primary/90 gap-1 text-white"
              >
                {editingTaskId ? (
                  <>
                    <Edit2 className="h-4 w-4" />
                    {isSubmitting ? "Saving…" : "Save"}
                  </>
                ) : (
                  <>
                    <Plus className="h-4 w-4" />
                    {isSubmitting ? "Creating…" : "Create"}
                  </>
                )}
              </Button>
            </div>
          </Form>
        </div>
      </div>
    </Modal>
  );
}
